import {
  resourceIdFilter,
  type FilterDefinition,
} from "@/shared/components/ResourceView";

/**
 * Comparators the RoboQL catalog accepts on tag-array fields. These are the
 * ones `TAG_FIELD_COMPARATORS` in `packages/roboql/src/roboql/model/core.py`
 * puts on `tags`, which the roboQL schema validator enforces before the
 * predicate ever reaches `SessionPredicateGenerator`.
 *
 * Deliberately not `setComparators` from the filter schema: that list is the
 * vocabulary the SavedFilters model accepts for a `set`-typed filter, pinned
 * to a different manifest by `FilterSchemaParity.test.ts`. The two hold the
 * same operators today but are independent definitions, and only this one
 * decides whether the Sessions query is accepted.
 * `coreSessionFilters.test.ts` fails if this list and the generated RoboQL
 * catalog disagree.
 */
const tagFieldComparators = ["CONTAINS", "NOT_CONTAINS"] as const;

/**
 * Filterable Sessions fields common to every Sessions list mount. Each
 * `field` is the roboQL token (alias or canonical label) the backend route
 * forwards to `SessionPredicateGenerator`.
 *
 * Mounts compose their picker by spreading this array and appending the
 * cross-aggregate id rows their scope admits: `sessionDatasetIdFilter`,
 * `sessionDeviceIdFilter`, or both.
 */
export const coreSessionFilters = [
  {
    type: "string",
    field: "id",
    name: "Session ID",
    ...resourceIdFilter,
  },
  { type: "string", field: "name", name: "Name" },
  { type: "date", field: "start_time", name: "Start time" },
  { type: "date", field: "end_time", name: "End time" },
  { type: "numeric", field: "duration", name: "Duration (ns)" },
  // Free-text input, with no `options` resolver because the backend does not
  // expose a Sessions tag autocomplete endpoint. Datasets/Events/Files
  // populate their tag picker via `getTagsForOrg`; until Sessions ships the
  // equivalent, users type tag values directly.
  {
    type: "string",
    field: "tags",
    name: "Tags",
    allowedComparators: tagFieldComparators,
  },
] as const satisfies readonly FilterDefinition[];

/**
 * The `dataset.dataset_id` row of the Sessions filter picker: narrows to
 * sessions holding at least one file from a given dataset. Appended by every
 * Sessions mount whose scope leaves the dataset axis free (the top-level
 * `/sessions` page, the device-scoped tab, and a collection's Sessions tab);
 * the dataset-scoped tab omits it because that axis is already pinned
 * server-side. Shared rather than repeated per mount so the label and the
 * roboQL field token cannot drift apart between pickers.
 */
export const sessionDatasetIdFilter = {
  type: "string",
  field: "dataset.dataset_id",
  name: "Dataset ID",
  ...resourceIdFilter,
} as const satisfies FilterDefinition;

/**
 * The `device.device_id` row of the Sessions filter picker: narrows to
 * sessions attached to a given device. Appended by every Sessions mount whose
 * scope leaves the device axis free (the top-level `/sessions` page, the
 * dataset-scoped tab, and a collection's Sessions tab); the device-scoped tab
 * omits it because that axis is already pinned server-side. Shared rather than
 * repeated per mount so the label and the roboQL field token cannot drift
 * apart between pickers.
 */
export const sessionDeviceIdFilter = {
  type: "string",
  field: "device.device_id",
  name: "Device ID",
  ...resourceIdFilter,
} as const satisfies FilterDefinition;
